import { useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactCard from '../components/ContactCard';

const contentHighlights = [
  {
    title: 'Content engines that scale',
    text: 'We plan and produce audience-first content that positions you as the go-to expert and drives qualified inbound demand.',
  },
  {
    title: 'Multi-channel, one workflow',
    text: 'Articles, video, podcasts, carousels, and email nurture — all coordinated under one editorial calendar.',
  },
  {
    title: 'Performance visibility',
    text: 'Dashboards connect content to pipeline metrics so you know what drives leads, bookings, and revenue.',
  },
];

const contentFormats = [
  'SEO articles & gated guides',
  'Video scripts, reels & UGC production',
  'Podcast planning & episode assets',
  'Email newsletters & nurture sequences',
  'Sales enablement decks & one-pagers',
  'Social media carousels & captioning',
];

const contentProcess = [
  {
    phase: 'Map',
    detail: 'Audience interviews, brand voice workshop, and competitive research inform your content pillars and calendar.',
  },
  {
    phase: 'Make',
    detail: 'Our strategists, writers, designers, and editors produce polished content that fits each channel perfectly.',
  },
  {
    phase: 'Multiply',
    detail: 'We repurpose every flagship asset across formats, distribute, and optimise using performance data.',
  },
];

export default function ContentCreationLanding() {
  const contactRef = useRef(null);
  const handleTalkClick = () => {
    contactRef.current?.focusForm?.();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-white via-sky-50 to-indigo-100 font-sans text-slate-900">
      <Navbar onTalkClick={handleTalkClick} />

      <main className="flex-1 pt-32">
        <section id="services" className="mx-auto max-w-6xl px-6 text-center md:text-left">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Content marketing, simplified</p>
              <h1 className="mt-4 text-4xl font-semibold md:text-5xl">Magnetic content that builds trust and drives demand</h1>
              <p className="mt-4 text-lg text-slate-600">
                We become your content engine — planning, creating, and distributing stories that attract customers, nurture
                relationships, and fuel every channel.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleTalkClick}
                  className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow hover:opacity-90"
                >
                  Get a content roadmap →
                </button>
                <a
                  href="tel:14374944736"
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition hover:bg-white"
                >
                  Talk now: 1‑437‑494‑4736
                </a>
              </div>
            </div>
            <div className="grid gap-4">
              {contentHighlights.map((highlight) => (
                <div key={highlight.title} className="rounded-3xl border border-slate-200 bg-white/70 p-5 shadow-sm backdrop-blur">
                  <h3 className="text-lg font-semibold text-slate-900">{highlight.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{highlight.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-2xl font-semibold text-slate-900">Everything your content program needs</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              {contentFormats.map((format) => (
                <div key={format} className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
                  {format}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="work" className="mx-auto mt-16 max-w-6xl px-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Success story</p>
              <h2 className="mt-2 text-3xl font-semibold text-slate-900">How we 4X’d inbound demos with content</h2>
              <p className="mt-4 text-sm text-slate-600">
                For a B2B SaaS platform, we build a thought-leadership engine combining blogs, webinars, and social video.
                In 6 months, organic demos quadrupled and sales cycles shortened thanks to educated leads.
              </p>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Thought leadership framework</p>
              <p className="mt-4 text-sm text-slate-600">
                Monthly executive interviews feed flagship articles, podcast scripts, LinkedIn carousels, and newsletter content,
                making it easy for your subject-matter experts to shine without writing a word.
              </p>
            </div>
          </div>
        </section>

        <section id="process" className="mx-auto mt-16 max-w-5xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900 text-center md:text-left">Our content engine workflow</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {contentProcess.map((step) => (
                <div key={step.phase} className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">{step.phase}</h3>
                  <p className="mt-3 text-sm text-slate-600">{step.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-4xl px-6 text-center">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900 md:text-4xl">Ready to turn content into revenue?</h2>
            <p className="mt-3 text-lg text-slate-600">
              Book a strategy session and we’ll map out the content pillars, formats, and distribution plan that will move the needle fastest.
            </p>
            <button
              type="button"
              onClick={handleTalkClick}
              className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-white shadow hover:opacity-90"
            >
              Build my content plan →
            </button>
          </div>
        </section>
      </main>

      <ContactCard ref={contactRef} className="mt-16" />
      <Footer />
    </div>
  );
}
